import { useEffect, useMemo, useState } from 'react';

import { getObjectDefinition } from '../../content/registry/objectRegistry';
import type { MapObjectInstance } from '../../content/schema/map';
import { diffInstances, type InstanceDiff } from '../store/mapEditorStore';
import { ObjectThumbnail } from '../thumbnails/ObjectThumbnail';
import type { ParamIssue } from '../validation/paramValidation';
import type { LintIssue } from '../validation/mapLinter';
import { TabStrip, panelId, tabId, type TabDescriptor } from './Tabs';

function TabCount({ n }: { n: number }) {
  return (
    <span style={{ background: 'var(--ss-hover)', borderRadius: 10, padding: '1px 8px', fontSize: '0.64rem' }}>{n}</span>
  );
}

interface Props {
  before: MapObjectInstance[];
  after: MapObjectInstance[];
  validationIssues: ParamIssue[];
  lintIssues: LintIssue[];
  onConfirm: () => void;
  onCancel: () => void;
  onRevertInstance: (instance: MapObjectInstance | null, id: string) => void;
  isSaving: boolean;
}

type Tab = 'added' | 'removed' | 'modified' | 'issues' | 'linter';

function formatPosition(position: [number, number, number]) {
  return `[${position[0].toFixed(2)}, ${position[2].toFixed(2)}]`;
}

export function PreSaveDiffModal({
  before,
  after,
  validationIssues,
  lintIssues,
  onConfirm,
  onCancel,
  onRevertInstance,
  isSaving,
}: Props) {
  const diff: InstanceDiff = useMemo(() => diffInstances(before, after), [before, after]);
  const errorCount = validationIssues.filter((issue) => issue.severity === 'error').length;
  const warningCount = validationIssues.filter((issue) => issue.severity === 'warning').length;

  const initialTab: Tab = diff.added.length > 0
    ? 'added'
    : diff.modified.length > 0
      ? 'modified'
      : diff.removed.length > 0
        ? 'removed'
        : errorCount > 0
          ? 'issues'
          : 'added';

  const [tab, setTab] = useState<Tab>(initialTab);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onCancel]);

  const totalChanges = diff.added.length + diff.removed.length + diff.modified.length;
  const beforeById = useMemo(() => new Map(before.map((inst) => [inst.id, inst])), [before]);

  const tabs: TabDescriptor[] = [
    { id: 'added', label: <>Added <TabCount n={diff.added.length} /></> },
    { id: 'modified', label: <>Modified <TabCount n={diff.modified.length} /></> },
    { id: 'removed', label: <>Removed <TabCount n={diff.removed.length} /></> },
    { id: 'issues', label: <>Issues <TabCount n={errorCount + warningCount} /></>, hasError: errorCount > 0 },
    { id: 'linter', label: <>Linter <TabCount n={lintIssues.length} /></>, hasError: lintIssues.some((i) => i.severity === 'error') },
  ];

  return (
    <div className="editor-modal-shroud" onClick={onCancel}>
      <div
        className="editor-modal editor-diff-modal ui-panel"
        onClick={(event) => event.stopPropagation()}
      >
        <header>
          <h3>Review changes before saving</h3>
          <p>
            {totalChanges} change{totalChanges === 1 ? '' : 's'} queued. Saving writes to the committed source file.
          </p>
        </header>

        <TabStrip listLabel="Save conflict sections" tabs={tabs} activeId={tab} onSelect={(id) => setTab(id as Tab)} controlsPanels />

        <div className="editor-diff-body" role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
          {tab === 'added' ? (
            <ul className="editor-diff-list">
              {diff.added.length === 0 ? <li className="editor-diff-empty">No new objects.</li> : null}
              {diff.added.map((instance) => {
                const def = getObjectDefinition(instance.assetId);
                return (
                  <li key={instance.id} className="editor-diff-row is-added">
                    <ObjectThumbnail assetId={instance.assetId} label={def?.name ?? instance.assetId} size={48} />
                    <div className="editor-diff-info">
                      <span className="editor-diff-name">{def?.name ?? instance.assetId}</span>
                      <span className="editor-diff-sub">{formatPosition(instance.transform.position)} · floor {instance.floor}</span>
                    </div>
                    <button
                      type="button"
                      className="editor-chip"
                      onClick={() => onRevertInstance(null, instance.id)}
                      title="Remove this new object from the save"
                    >
                      Revert
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {tab === 'modified' ? (
            <ul className="editor-diff-list">
              {diff.modified.length === 0 ? <li className="editor-diff-empty">No modifications.</li> : null}
              {diff.modified.map(({ before: b, after: a, changes }) => {
                const def = getObjectDefinition(a.assetId);
                return (
                  <li key={a.id} className="editor-diff-row is-modified">
                    <ObjectThumbnail assetId={a.assetId} label={def?.name ?? a.assetId} size={48} />
                    <div className="editor-diff-info">
                      <span className="editor-diff-name">{def?.name ?? a.assetId}</span>
                      <span className="editor-diff-sub">
                        {changes.join(', ')} · {formatPosition(b.transform.position)} → {formatPosition(a.transform.position)}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="editor-chip"
                      onClick={() => onRevertInstance(b, a.id)}
                      title="Restore this object to its source state"
                    >
                      Revert
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {tab === 'removed' ? (
            <ul className="editor-diff-list">
              {diff.removed.length === 0 ? <li className="editor-diff-empty">No removals.</li> : null}
              {diff.removed.map((instance) => {
                const def = getObjectDefinition(instance.assetId);
                return (
                  <li key={instance.id} className="editor-diff-row is-removed">
                    <ObjectThumbnail assetId={instance.assetId} label={def?.name ?? instance.assetId} size={48} />
                    <div className="editor-diff-info">
                      <span className="editor-diff-name">{def?.name ?? instance.assetId}</span>
                      <span className="editor-diff-sub">was at {formatPosition(instance.transform.position)} · floor {instance.floor}</span>
                    </div>
                    <button
                      type="button"
                      className="editor-chip"
                      onClick={() => onRevertInstance(instance, instance.id)}
                      title="Put this object back in the scene"
                    >
                      Restore
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {tab === 'issues' ? (
            <ul className="editor-diff-list">
              {validationIssues.length === 0 ? <li className="editor-diff-empty">No validation issues detected.</li> : null}
              {validationIssues.map((issue, index) => {
                const instance = beforeById.get(issue.instanceId) ?? null;
                return (
                  <li
                    key={`${issue.instanceId}-${issue.key}-${index}`}
                    className={`editor-diff-row editor-issue is-${issue.severity}`}
                  >
                    <ObjectThumbnail assetId={issue.assetId} label={issue.assetId} size={48} />
                    <div className="editor-diff-info">
                      <span className="editor-diff-name">{issue.assetId} · {issue.key}</span>
                      <span className="editor-diff-sub">{issue.message}</span>
                    </div>
                    {instance ? (
                      <button
                        type="button"
                        className="editor-chip"
                        onClick={() => onRevertInstance(instance, issue.instanceId)}
                      >
                        Revert
                      </button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}

          {tab === 'linter' ? (
            <ul className="editor-diff-list">
              {lintIssues.length === 0 ? (
                <li className="editor-diff-empty">No structural issues detected.</li>
              ) : null}
              {lintIssues.map((issue, index) => (
                <li
                  key={`lint-${issue.code}-${issue.instanceId ?? issue.triggerId ?? index}`}
                  className={`editor-diff-row editor-issue is-${issue.severity}`}
                >
                  <div className="editor-diff-info">
                    <span className="editor-diff-name">
                      [{issue.severity.toUpperCase()}] {issue.code}
                    </span>
                    <span className="editor-diff-sub">{issue.message}</span>
                  </div>
                  {issue.instanceId ? (
                    <button
                      type="button"
                      className="editor-chip"
                      onClick={() => {
                        const inst = beforeById.get(issue.instanceId!) ?? null;
                        onRevertInstance(inst, issue.instanceId!);
                      }}
                    >
                      Inspect
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <footer>
          <button type="button" className="editor-action-btn" onClick={onCancel} disabled={isSaving}>
            Cancel
          </button>
          <button
            type="button"
            className="editor-action-btn is-primary"
            onClick={onConfirm}
            disabled={isSaving || errorCount > 0 || lintIssues.some((i) => i.severity === 'error')}
            title={
              errorCount > 0
                ? 'Resolve validation errors before saving.'
                : lintIssues.some((i) => i.severity === 'error')
                  ? 'Resolve linter errors before saving.'
                  : 'Write to source file'
            }
          >
            {isSaving ? 'Saving…' : `Save to source file${totalChanges > 0 ? ` (${totalChanges})` : ''}`}
          </button>
        </footer>
      </div>
    </div>
  );
}
